import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ChartConfigurationCustomTypesPerDataset, ChartEvent, LegendElement, LegendItem } from "chart.js";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { distinct, takeUntil } from "rxjs/operators";

const enum ParameterName
{
    ConsumptionChartVendorServiceType = "ConsumptionChartVendorServiceType"
}

const enum AdditionalChartOptions
{
    DefaultSelectionForServiceType = "DefaultSelectionForServiceType"
}

@Component({
    selector: "utilities-prepaid-consumption-chart",
    templateUrl: "./prepaidConsumptionChart.tmpl.html",
    styleUrls: [
        "./prepaidConsumptionChart.component.scss"
    ],
    standalone: false
})
export class PrepaidConsumptionChartComponent implements OnInit, OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();

    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfigurationCustomTypesPerDataset;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;

    private readonly serviceTypesSubject = new BehaviorSubject<Array<SettingLookupOption>>([]);

    readonly formGroup: FormGroup;

    isLoaded: boolean;
    errorMessage: string | undefined;

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $loggingService: LoggingService,
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.formGroup = new FormGroup({
            serviceType: LgdFormControlFactory.select<SettingLookupOption>(
                undefined,
                false,
                {
                    displayValueFn: x => x?.Display
                }),
        });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    get chartCriteria(): IChartCriteria | undefined { return this._chartCriteria || this.customDataBinding?.chartCriteria; }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get serviceTypeControl(): AbstractControl<SettingLookupOption> { return this.formGroup.get("serviceType"); }
    private get selectedServiceType(): SettingLookupOption { return this.serviceTypeControl.value; }

    get serviceTypesObservable(): Observable<Array<SettingLookupOption>> { return this.serviceTypesSubject.asObservable(); }

    get chartHasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        (this.chartHasData
            ? new Promise(() =>
            {
                this.updateServiceTypeControl();
            })
            : this.loadChartData())
            .then(
                () =>
                {
                    this.updateChartConfiguration();
                }, e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        subscribeToValueChanges(this.serviceTypeControl, (_newValue: SettingLookupOption) =>
        {
            this.addServiceTypeParameterToCriteria();

            this.loadChartData();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(
                takeUntil(self.destructor$),
                distinct())
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    this.errorMessage = undefined;

                    this.updateServiceTypeControl();
                })
            .then(
                () =>
                {
                    if (!this.chartHasData)
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.errorMessage = error.error.Message;
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private updateServiceTypeControl(): void
    {
        const serviceTypes = this.chartCriteria.Parameters
            .find(x => x.Id === ParameterName.ConsumptionChartVendorServiceType)?.Options;
        if (serviceTypes)
        {
            this.serviceTypesSubject.next(serviceTypes);
            this.serviceTypeControl.setValue(serviceTypes[0], { emitEvent: false });
        }
    }

    private addServiceTypeParameterToCriteria(): void
    {
        let serviceTypeValue = this.chartCriteria.Parameters?.find(x => x.Id === ParameterName.ConsumptionChartVendorServiceType) as ProfileSettingViewModel;
        if (!serviceTypeValue)
        {
            serviceTypeValue = {
                Id: ParameterName.ConsumptionChartVendorServiceType,
            };
            this.chartCriteria?.Parameters?.push(serviceTypeValue);
        }
        serviceTypeValue.Value = this.serviceTypeControl.value;
    }

    private updateChartConfiguration(): void
    {
        this.addServiceTypeParameterToCriteria();
        this.displayChart();

        const defaultServiceTypeValue = this.chartCriteria.Parameters
            .find(x => x.Id === ParameterName.ConsumptionChartVendorServiceType)
            ?.AdditionalAttributes[AdditionalChartOptions.DefaultSelectionForServiceType] as string;
        if (defaultServiceTypeValue
            && this.selectedServiceType?.Value !== defaultServiceTypeValue)
        {
            const serviceType = this.serviceTypesSubject.value?.find(x => x.Value === defaultServiceTypeValue);
            this.serviceTypeControl.setValue(serviceType, { emitEvent: false });
        }
    }

    private displayChart(): void
    {
        this.chartConfig = {
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, color: "#333333" },
                        onClick: (_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const meta = legend.chart.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !legend.chart.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (legend.chart.data.datasets || []).length; ++i)
                            {
                                const iMeta = legend.chart.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            legend.chart.options.scales[yAxisID].display = showScale;
                            legend.chart.update();
                        },
                    },
                    tooltip: {
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    }
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    leftYAxis: {
                        type: "linear",
                        display: true,
                        position: "left",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(
                                    value > -10 && value < 10
                                        ? value.toFixed(2)
                                        : value.toFixed(0));
                            },
                        },
                    },
                    rightYAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: false },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) => value,
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${this.chartCriteria.ChartDatas[0].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[0].Data,
                            backgroundColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            borderColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            hoverBackgroundColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            hoverBorderColor: this.chartCriteria.ChartDatas[0].Additional[0],
                            yAxisID: "leftYAxis",
                            categoryPercentage: 0.5,
                            barPercentage: 0.8,
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[1].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[1].Data,
                            borderColor: this.chartCriteria.ChartDatas[1].Additional[0],
                            backgroundColor: this.chartCriteria.ChartDatas[1].Additional[0],
                            yAxisID: "rightYAxis",
                        },
                        {
                            type: "line",
                            label: `${this.chartCriteria.ChartDatas[2].SetLabel}`,
                            data: this.chartCriteria.ChartDatas[2].Data,
                            fill: false,
                            borderColor: this.chartCriteria.ChartDatas[2].Additional[0],
                            backgroundColor: this.chartCriteria.ChartDatas[2].Additional[0],
                            pointBorderColor: this.chartCriteria.ChartDatas[2].Additional[0],
                            pointBackgroundColor: this.chartCriteria.ChartDatas[2].Additional[0],
                            pointHoverBackgroundColor: this.chartCriteria.ChartDatas[2].Additional[0],
                            pointHoverBorderColor: this.chartCriteria.ChartDatas[2].Additional[0],
                            yAxisID: "rightYAxis",
                        }
                    ]
                    : [],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    displayChartParameterDialogAndReload(): void
    {
        this.$chartDialogService.show(this.chartCriteria.Parameters, "Prepaid Consumption")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (!Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.chartCriteria.Parameters = result;
                    return this.loadChartData();
                }
            });
    }
}
